/**
 * What the operator is told after pressing STOP (#574).
 *
 * A STRING HELPER RATHER THAN INLINE BRANCHES, for the reason `missionRouteLockReason` gives
 * beside it: the claim is testable without mounting the COP. And the claim matters more here
 * than on most surfaces - one of these outcomes says a sortie is over, and saying that about a
 * mission whose aircraft is still descending is the exact defect STOP exists to remove.
 *
 * The four cases the server can answer with, and why each reads the way it does:
 *
 *  - `completed`         the mission is over and nothing was in the air. The only green
 *                        "it is done" in the set.
 *  - `returning`         the aircraft was commanded home. The mission is STILL in progress,
 *                        so the copy says what is going to happen, never that it has.
 *  - `already_returning` a return was already flying and nothing was commanded a second time.
 *                        Not a failure - the operator asked for the aircraft to come home and
 *                        it is coming home - but they must not be left thinking this press is
 *                        what started it.
 *  - `already_landing`   a tag-guided descent was already landing the aircraft on its pad, by the
 *                        app's own acks, and nothing was commanded: a second return mid-descent
 *                        cost 3 m and a second acquisition on 2026-09-14.
 *  - anything else       NOT reported as a completion. The `else` branch used to assert
 *                        "Mission completed", which is the one claim that must never be wrong,
 *                        on the one input nobody has checked: a payload change, a proxy-rewritten
 *                        body, or a third outcome added later all land in it.
 */
export type MissionStopTone = 'success' | 'warning' | 'error';

export interface MissionStopMessage {
  tone: MissionStopTone;
  title: string;
  description?: string;
  /** Milliseconds. Everything carrying an instruction outlives the 4 s toast default. */
  duration?: number;
  /** Should the row show RETURNING - i.e. is an aircraft on its way home because of this? */
  marksReturning: boolean;
}

/**
 * `completes_on_landing: false` means the aircraft is coming home but NO open sortie is bound
 * to this mission, so `closeFlight` will never reach it. Told plainly, because the alternative
 * is a mission that sits `in_progress` for ever holding its airframe out of every later launch.
 */
const LANDING_CLOSES = 'The mission stays in progress and completes when the aircraft is down.';
const LANDING_DOES_NOT_CLOSE =
  'No sortie is bound to this mission, so it will NOT close itself - end it again once the aircraft is down.';

export function missionStopMessage(input: {
  /** The mission and aircraft, already formatted - every message names them. */
  who: string;
  outcome: string | undefined;
  /** The server's `completes_on_landing`; absent is treated as the promise holding. */
  completesOnLanding?: boolean;
}): MissionStopMessage {
  const { who, outcome } = input;
  const closes = input.completesOnLanding !== false;
  const landingNote = closes ? LANDING_CLOSES : LANDING_DOES_NOT_CLOSE;

  if (outcome === 'returning' || outcome === 'already_returning' || outcome === 'already_landing') {
    return {
      // `warning` when the promise does NOT hold, and the difference is not decoration: under
      // `richColors` a success toast prints its description in the green this product reserves
      // for "all good", so the one caution in the flow would be typeset as reassurance.
      tone: closes ? 'success' : 'warning',
      title: outcome === 'already_returning'
        ? `${who} - already returning`
        : outcome === 'already_landing'
          ? `${who} - already landing on the pad`
          : `${who} - commanded home`,
      description: landingNote,
      duration: closes ? 10_000 : 15_000,
      marksReturning: true,
    };
  }

  if (outcome === 'completed') {
    return { tone: 'success', title: `${who} - mission completed`, marksReturning: false };
  }

  return {
    tone: 'warning',
    title: `${who} - stop accepted`,
    description: 'The server did not say what it did. Reload the queue and confirm.',
    duration: 15_000,
    marksReturning: false,
  };
}

/**
 * The refusal's message, and whether the aircraft may nonetheless be coming home.
 *
 * A COMMAND TIMEOUT IS NOT A FAILURE, and the mission door has said so since #367: mqtt.js
 * replays the unacked packet on reconnect, so the aircraft may still fly the return. Rendered
 * red like every other error, the operator's reflex is to press again - which is the one thing
 * the message asks them not to do, and which `planDockReturn` grants, because an operator
 * outranks its running marker. So it is amber, it lasts long enough to read, and it marks the
 * row RETURNING: the honest state is "this may be happening", and the row saying nothing is a
 * worse answer than the row saying it might.
 */
export function missionStopFailure(input: {
  who: string;
  message: string;
  /** The server's error `code`, not the status - so a future 504 here inherits nothing. */
  code?: string;
}): MissionStopMessage {
  if (input.code === 'command_timeout') {
    return {
      tone: 'warning',
      title: `${input.who} - return not acknowledged`,
      description: input.message,
      duration: 20_000,
      marksReturning: true,
    };
  }
  return {
    tone: 'error',
    title: `${input.who} - not stopped`,
    description: input.message,
    duration: 15_000,
    marksReturning: false,
  };
}
